import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../lib/api";
import type { EarlierEntry, WorkHistory as History } from "../lib/api-client/workHistory";
import type { WorkTarget } from "../lib/api-client/workReports";
import { BouncyAccordion } from "./beui/BouncyAccordion";

const time = (value: string | null) => value && Number.isFinite(Date.parse(value))
  ? `${new Date(value).toLocaleString("en-PH", { timeZone: "Asia/Manila", dateStyle: "medium", timeStyle: "short" })} PHT` : "Time unknown";
const accessLost = (error: unknown) => error instanceof ApiError && (error.status === 401 || error.status === 404
  || error.status === 403 && !["legal_reacceptance_required", "company_verification_required"].includes(error.code || ""));

function EarlierNote({ entry, label }: { entry: EarlierEntry; label: string }) {
  return <li className="text-xs">
    <p className="font-semibold text-navy">{label}</p><p className="whitespace-pre-wrap [overflow-wrap:anywhere]">{entry.note}</p>
    <p className="mt-1 text-xs text-mut">{entry.authorName || "Unknown author"} · {entry.byRole || "Unknown role"} · {time(entry.createdAt)}</p>
  </li>;
}

/** A completion snapshot has partial, sometimes irregular historical shape. Render its recorded fields
 * without converting snapshot-only values into current checklist items or inventing missing provenance. */
function SnapshotFields({ value }: { value: unknown }) {
  if (value === null || value === undefined) return <span className="text-mut">Unknown</span>;
  if (typeof value !== "object") return <span className="whitespace-pre-wrap [overflow-wrap:anywhere]">{String(value)}</span>;
  if (Array.isArray(value)) return value.length ? <ol className="space-y-2">{value.map((entry, index) => <li key={index} className="border-l-2 border-line pl-2"><SnapshotFields value={entry} /></li>)}</ol> : <span>None recorded</span>;
  return <dl className="space-y-2">{Object.entries(value).map(([key, field]) => <div key={key}>
    <dt className="text-xs font-semibold text-mut">{key.replace(/([a-z])([A-Z])/g, "$1 $2")}</dt>
    <dd>{typeof field === "string" && ["frozenAt", "at", "doneAt", "releasedAt"].includes(key)
      ? <time dateTime={field}>{time(field)}</time> : <SnapshotFields value={field} />}</dd>
  </div>)}</dl>;
}

/** Callers key the whole Work workspace by record/account/profile. Read generations prevent a delayed
 * history reply from restoring content after lifecycle refresh or definitive access loss. */
export function WorkHistory({ kind, id, refreshKey, onAccessLost }: WorkTarget & { refreshKey: string; onAccessLost: () => void }) {
  const [history, setHistory] = useState<History | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const active = useRef(false);
  const generation = useRef(0);
  const load = useCallback(async () => {
    const started = ++generation.current;
    setLoading(true); setError(null);
    try {
      const next = await api.workHistory({ kind, id });
      if (active.current && started === generation.current) setHistory(next);
    } catch (failure) {
      if (!active.current || started !== generation.current) return;
      if (accessLost(failure)) { setHistory(null); generation.current++; setLoading(false); onAccessLost(); }
      setError(failure instanceof Error ? failure.message : "Could not load work history.");
    } finally { if (active.current && started === generation.current) setLoading(false); }
  }, [kind, id, onAccessLost]);
  useEffect(() => {
    active.current = true; void load();
    return () => { active.current = false; generation.current++; };
  }, [load, refreshKey]);
  const legacy = history?.legacy;
  const hasEarlierWork = !!legacy && (legacy.checkins.length > 0 || legacy.progress.length > 0 || legacy.timeLogs.length > 0);
  if (!loading && !error && history && !hasEarlierWork && history.completionSnapshot == null) return null;
  const records = hasEarlierWork && legacy && <div className="space-y-3 text-navy">
    <p className="text-xs text-mut">Earlier profile details and action sequences may be unknown. Typed progress labels are retained as recorded.</p>
    <ol className="space-y-2">{legacy.checkins.map(entry => <EarlierNote key={entry.id} entry={entry} label="Check-in" />)}
      {legacy.progress.map(entry => <EarlierNote key={entry.id} entry={entry} label={`Progress · ${entry.logType}`} />)}</ol>
    {legacy.timeLogs.length > 0 && <div><h3 className="mb-2 text-sm font-semibold">Past time logs</h3><ol className="space-y-2">{legacy.timeLogs.map(log => <li key={log.id} className="text-xs">
      <p className="font-semibold">{log.clockOut ? log.minutes == null ? "Duration unknown" : `${log.minutes} minutes recorded` : "Unfinished · finish and duration unknown"}</p>
      <p className="text-xs text-mut">{log.authorName || "Unknown author"} · Start: {time(log.clockIn)}</p>
      {log.clockOut && <p className="text-xs text-mut">Finish: {time(log.clockOut)}</p>}
      {log.note && <p className="mt-1 whitespace-pre-wrap [overflow-wrap:anywhere]">{log.note}</p>}
    </li>)}</ol></div>}
  </div>;
  return <section aria-label={history && !hasEarlierWork ? "Work history" : "Earlier work"} className="wu-history">
    {loading && <p role="status" className="text-sm text-mut">Loading earlier work…</p>}
    {error && <p role="alert" className="text-sm text-brand-red">{error} <button type="button" className="underline" onClick={() => void load()}>Retry</button></p>}
    {history && !error && <BouncyAccordion classNames={{ item: "bg-white", trigger: "wu-history-trigger", title: "text-[11px] font-semibold", description: "wu-history-content", content: "[&>div]:px-0 [&>div]:pb-0" }} items={[
      ...(hasEarlierWork ? [{ id: "earlier", title: "Earlier work", description: records }] : []),
      ...(history.completionSnapshot == null ? [] : [{ id: "snapshot", title: "Completion snapshot", description: <>
        <p className="mb-3 text-xs text-mut">Available partial completion evidence. It does not contain the complete work history; authors and earlier actions may be unknown.</p>
        <SnapshotFields value={history.completionSnapshot} /></> }]),
    ]} />}
  </section>;
}
